import FormErrors from './FormErrors';

class Form {
  constructor(data, inputTypes, resetByDefault = true, valueHandling = 'default', emptyValueHandling = 'default') {
    this.originalData = data;
    this.inputTypes = inputTypes;
    this.additionalData = {};
    this.resetByDefault = resetByDefault;
    this.valueHandling = valueHandling;
    this.emptyValueHandling = emptyValueHandling;
    this.hasFiles = false;
    this.fileAction = undefined;
    this.responseData = null;

    for (const field in data) {
      this[field] = data[field];
    }

    this.errors = new FormErrors();

    this.$el = undefined;
  }

  setEl($el) {
    this.$el = $el;
  }

  strictValueCheck(value) {
    if (value === null || value === '') {
      return false;
    } if (Array.isArray(value)) {
      return value.length > 0;
    } if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return value !== null && value !== '';
  }

  getDataType(prefix) {
    const keys = (prefix ?? '').replace(/]/g, '').split('[').filter((key) => [null, ''].indexOf(key) === -1);
    let dataType = null;

    let dataTypes = this.inputTypes;

    for (let i = 0; i < keys.length; i++) {
      if (typeof dataTypes[keys[i]] === 'object') {
        dataTypes = dataTypes[key[i]];
      } else if (typeof dataTypes[keys[i]] === 'undefined') {
        dataType = null;
        break;
      } else if (typeof dataTypes[keys[i]] === 'string') {
        dataType = dataTypes[keys[i]];
      }
    }

    return dataType;
  }

  dataRecursive(inputData, prefix = null, allowFiles = false) {
    let outputData = {};

    if (inputData === null) {
      return inputData;
    }

    if (this.getDataType(prefix) === 'date-range' && typeof inputData === 'object') {
      const dateRangeFormatted = {};
      ['start', 'end'].forEach((dateInx) => {
        if (typeof inputData[dateInx] === 'object' && inputData[dateInx] instanceof Date) {
          dateRangeFormatted[dateInx] = inputData[dateInx].format('Y-m-d H:i:s');
        } else {
          dateRangeFormatted[dateInx] = inputData[dateInx];
        }
      });

      return dateRangeFormatted;
    }

    if (Array.isArray(inputData)) {
      outputData = [];
    }

    if (typeof inputData !== 'object' || inputData === null) {
      return inputData;
    }

    if (!allowFiles && inputData instanceof File) {
      return null;
    } if (allowFiles && inputData instanceof File) {
      return inputData;
    }

    for (const property in { ...inputData }) {
      const elements = this.$el.querySelectorAll(`[name="${prefix}[${property}]"]`);
      let value = null;
      if (elements.length) {
        value = inputData[property];
        if (this.emptyValueHandling === 'strict' && this.strictValueCheck(value)) {
          outputData[property] = value;
        } else if (this.emptyValueHandling === 'default') {
          outputData[property] = value;
        }
      } else if (Array.isArray(inputData)) {
        value = this.dataRecursive(inputData[property], `${prefix}[${property}]`);
        if (this.emptyValueHandling === 'strict' && this.strictValueCheck(value)) {
          if (this.valueHandling === 'strict') {
            outputData[property] = value;
          } else {
            outputData.push(value);
          }
        } else if (this.emptyValueHandling === 'default') {
          if (this.valueHandling === 'strict') {
            outputData[property] = value;
          } else {
            outputData.push(value);
          }
        }
      } else if (typeof inputData[property] === 'object') {
        value = this.dataRecursive(inputData[property], `${prefix}[${property}]`);
        if (this.emptyValueHandling === 'strict' && this.strictValueCheck(value)) {
          outputData[property] = value;
        } else if (this.emptyValueHandling === 'default') {
          outputData[property] = value;
        }
      } else if (this.emptyValueHandling === 'strict' && this.strictValueCheck(inputData[property])) {
        outputData[property] = inputData[property];
      } else if (this.emptyValueHandling === 'default') {
        outputData[property] = inputData[property];
      }
    }

    return outputData;
  }

  appendArray(formData, value, name) {
    if (!value && name) {
      formData.append(name, '');
    } else if (typeof value === 'object') {
      if (!(value instanceof File)) {
        for (const key in value) {
          if (typeof value[key] === 'object') formData = this.appendArray(formData, value[key], `${name}[${key}]`);
          else formData.append(`${name}[${key}]`, value[key]);
        }
      } else {
        formData.append(name, value);
      }
    } else {
      formData.append(name, value);
    }

    return formData;
  }

  filterArray(arr) {
    return arr.filter((val) => JSON.stringify(val) !== '{}');
  }

  data() {
    const formData = {};

    for (const property in { ...this.originalData }) {
      let val;
      if (this.$el !== undefined) {
        val = this.dataRecursive(this[property], property);
      } else {
        val = this[property];
      }

      if (!(val instanceof File)) {
        if (this.emptyValueHandling === 'strict') {
          if (Array.isArray(val)) {
            val = this.filterArray(val);
          }
          if (this.strictValueCheck(val)) {
            formData[property] = val;
          }
        } else {
          formData[property] = val;
        }
      }
    }

    for (const property in this.additionalData) {
      if (this.additionalData[property] !== null) {
        formData[property] = this.additionalData[property];
      }
    }

    return formData;
  }

  fileData() {
    let formData = new FormData();
    for (const property in this.originalData) {
      let val;
      if (this.$el !== undefined) {
        val = this.dataRecursive(this[property], property, true);
      } else {
        val = this[property];
      }

      if (val !== null) {
        if (typeof val === 'object' && !(val instanceof File)) {
          formData = this.appendArray(formData, val, property);
        } else {
          formData.append(property, val);
        }
      }
    }

    const fileFormData = new FormData();
    for (const pair of formData.entries()) {
      if (pair[1] instanceof File) {
        fileFormData.append(pair[0], pair[1]);
      }
    }

    for (const property in this.additionalData) {
      if (this.additionalData[property] !== null && property !== '_method') {
        fileFormData.append(property, this.additionalData[property]);
      }
    }

    return fileFormData;
  }

  put($key, $val) {
    this.additionalData[$key] = $val;
  }

  reset() {
    for (const field in this.originalData) {
      this[field] = null;
    }

    this.errors.clear();
  }

  post(url) {
    return this.submit('post', url, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  get(url) {
    return this.submit('get', url, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  postFiles() {
    return this.submit('post', this.fileAction, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  delete(url) {
    return this.submit('delete', url);
  }

  submit(requestType, url) {
    return new Promise((resolve, reject) => {
      axios[requestType](url, requestType === 'post' ? this.data() : { params: this.data() })
        .then((response) => {
          this.responseData = response.data;
          if (this.hasFiles && this.fileAction !== undefined) {
            this.submitFiles().then(() => {
              this.onSuccess();

              resolve(this.responseData);
            }).catch((error) => {
              let errors = {};
              if (typeof error !== 'undefined') {
                errors = error.response.data.errors;
              }
              this.onFail(errors);

              reject(errors);
            });
          } else {
            this.onSuccess();

            resolve(this.responseData);
          }
        })
        .catch((error) => {
          this.onFail(error.response.data.errors);

          reject(error.response.data.errors);
        });
    });
  }

  submitFiles() {
    return new Promise((resolve, reject) => {
      const fileData = this.fileData();
      if (typeof this.responseData.fileData !== 'undefined') {
        for (const key in this.responseData.fileData) {
          fileData.append(key, this.responseData.fileData[key]);
        }
      }

      axios.post(this.fileAction, fileData)
        .then((response) => {
          if (typeof response.data !== 'undefined') {
            this.responseData = Object.assign(this.responseData, response.data);
          }
          this.onSuccess();

          resolve(this.responseData);
        })
        .catch((error) => {
          this.onFail(error.response.data.errors);

          reject(error.response.data.errors);
        });
    });
  }

  onSuccess() {
    if (this.resetByDefault) {
      this.reset();
    }

    if (typeof this.responseData.redirectTo !== 'undefined') {
      window.location.href = this.responseData.redirectTo;
    }
  }

  onFail(errors) {
    this.errors.record(errors);
  }
}

export default Form;
